import { useState } from 'react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import GetAppIcon from '@material-ui/icons/GetApp';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import type { MarketplaceItem } from './types';
import { isWindsurfSupportedType } from './typeMetadata';

const DEFAULT_VSIX_URL = 'https://turbo-couscous-8enj3p4.pages.github.io/verint-marketplace-installer.vsix';
const DEFAULT_WINDSURF_VSIX_URL = 'https://turbo-couscous-8enj3p4.pages.github.io/marketplace-installer-windsurf-0.0.1.vsix';
const DEFAULT_INTELLIJ_ZIP_URL = 'https://turbo-couscous-8enj3p4.pages.github.io/verint-intellij-installer-setup.zip';

const useStyles = makeStyles(theme => ({
  dialogPaper: { borderRadius: 12 },
  titleBar: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(2),
  },
  content: { paddingTop: theme.spacing(3), paddingBottom: theme.spacing(4) },
  accordion: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px !important',
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f8fafc',
    '&:before': { display: 'none' },
    '&.MuiAccordion-root': { boxShadow: 'none' },
  },
  accordionSummaryContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  iconBox: {
    width: 40,
    height: 40,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${theme.palette.divider}`,
    fontSize: '1.3rem',
    fontWeight: 700,
  },
  codeBlock: {
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    padding: theme.spacing(1.5),
    borderRadius: 4,
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': { backgroundColor: '#334155' },
  },
  ideCard: {
    padding: theme.spacing(4),
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    textAlign: 'center' as const,
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: 8,
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  ideCardVscode: { '&:hover': { borderColor: '#3b82f6', boxShadow: '0 4px 12px rgba(59,130,246,0.1)' } },
  ideCardIntellij: { '&:hover': { borderColor: '#F01E69', boxShadow: '0 4px 12px rgba(240,30,105,0.1)' } },
  ideIcon: { fontSize: '2.5rem', marginBottom: theme.spacing(2), display: 'flex', alignItems: 'center', justifyContent: 'center' },
  btnVscode: {
    marginTop: 'auto',
    backgroundColor: '#3b82f6',
    color: '#fff',
    textTransform: 'none' as const,
    fontWeight: 600,
    padding: theme.spacing(1.5, 0),
    '&:hover': { backgroundColor: '#2563eb' },
  },
  btnIntellij: {
    marginTop: 'auto',
    backgroundColor: '#F01E69',
    color: '#fff',
    textTransform: 'none' as const,
    fontWeight: 600,
    padding: theme.spacing(1.5, 0),
    '&:hover': { backgroundColor: '#D91C5C' },
  },
  btnWindsurf: {
    marginTop: 'auto',
    backgroundColor: '#7c3aed',
    color: '#fff',
    textTransform: 'none' as const,
    fontWeight: 600,
    padding: theme.spacing(1.5, 0),
    '&:hover': { backgroundColor: '#6d28d9' },
  },
  ideCardWindsurf: { '&:hover': { borderColor: '#7c3aed', boxShadow: '0 4px 12px rgba(124,58,237,0.1)' } },
  btnDownload: {
    textTransform: 'none' as const,
    fontWeight: 600,
    backgroundColor: '#3b82f6',
    color: '#fff',
    '&:hover': { backgroundColor: '#2563eb' },
  },
  btnDownloadWindsurf: {
    textTransform: 'none' as const,
    fontWeight: 600,
    backgroundColor: '#7c3aed',
    color: '#fff',
    '&:hover': { backgroundColor: '#6d28d9' },
  },
  unavailableNote: {
    marginTop: theme.spacing(1.25),
    fontSize: '0.78rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
}));

interface InstallModalProps {
  open: boolean;
  onClose: () => void;
  item: MarketplaceItem | null;
  /** Resolved base URL for the marketplace backend (e.g. http://localhost:7007/api/marketplace) */
  backendBaseUrl?: string;
  /** Callback to track a download event (fired after deep-link is triggered) */
  onTrackDownload?: (ide: string) => void;
}

export function InstallModal({ open, onClose, item, backendBaseUrl: _backendBaseUrl, onTrackDownload }: InstallModalProps) {
  const classes = useStyles();
  const configApi = useApi(configApiRef) as { getOptionalString?: (key: string) => string | undefined };
  const [copied, setCopied] = useState(false);

  if (!item) return null;

  const vsixUrl = configApi.getOptionalString?.('aiMarketplace.installers.vsixUrl')?.trim() || DEFAULT_VSIX_URL;
  const windsurfVsixUrl = configApi.getOptionalString?.('aiMarketplace.installers.windsurfVsixUrl')?.trim() || DEFAULT_WINDSURF_VSIX_URL;
  const intellijZipUrl = configApi.getOptionalString?.('aiMarketplace.installers.intellijZipUrl')?.trim() || DEFAULT_INTELLIJ_ZIP_URL;

  // Determine whether the item has a proper installable file (not a README fallback)
  const hasInstallableFile = !!(
    ((item.agentMdFile || item.contentFile) && item.rawBaseUrl) ||
    (item.type === 'template' && item.templateFiles?.length && item.rawBaseUrl)
  );
  const hasVsixAsset = Boolean(vsixUrl);
  const hasWindsurfAsset = Boolean(windsurfVsixUrl);
  const hasIntelliJAsset = Boolean(intellijZipUrl);
  const canLaunchVsCode = hasInstallableFile && hasVsixAsset;
  const canLaunchWindsurf = hasInstallableFile && hasWindsurfAsset;
  const canLaunchIntelliJ = hasInstallableFile && hasIntelliJAsset;
  /** Only show Windsurf section for artifact types it can install. */
  const isWindsurfSupported = isWindsurfSupportedType(item.type);
  const vsCodeUnavailableReason = !hasInstallableFile
    ? 'No installable file is available for this item yet.'
    : !hasVsixAsset
      ? 'The VS Code installer extension is not configured for this environment yet.'
      : '';
  const windsurfUnavailableReason = !hasInstallableFile
    ? 'No installable file is available for this item yet.'
    : !hasWindsurfAsset
      ? 'The Windsurf installer extension is not configured for this environment yet.'
      : '';
  const intellijUnavailableReason = !hasInstallableFile
    ? 'No installable file is available for this item yet.'
    : !hasIntelliJAsset
      ? 'The IntelliJ installer package is not configured for this environment yet.'
      : '';

  /** Direct raw GitHub URL for IDE deep-links (extensions authenticate with their own GitHub token). */
  const buildDirectUrl = () => {
    const file = item.agentMdFile || item.contentFile;
    if (!file || !item.rawBaseUrl) return undefined;
    return `${item.rawBaseUrl}/${file}`;
  };

  /** Build a single batch-install deep-link URI.
   *  Only passes file names + types (short) and the rawBaseUrl separately to keep URI small.
   *  The extension constructs full download URLs from rawBaseUrl + name. */
  const buildBatchUri = (scheme: string, files: Array<{ name: string; type: string }>, slug: string) => {
    const items = JSON.stringify(files.map(f => ({ name: f.name, type: f.type })));
    const rawBase = item.rawBaseUrl || '';
    return `${scheme}/install-batch?items=${encodeURIComponent(items)}&slug=${encodeURIComponent(slug)}&rawBaseUrl=${encodeURIComponent(rawBase)}`;
  };

  const handleDeepLink = (scheme: 'vscode' | 'vscode-insiders') => {
    // Template multi-file install — single batch URI, one browser prompt
    if (item.type === 'template' && item.templateFiles?.length && item.rawBaseUrl) {
      const uri = buildBatchUri(`${scheme}://verint.marketplace-installer`, item.templateFiles, item.name);
      window.location.assign(uri);
      onTrackDownload?.(scheme);
      return;
    }

    const contentUrl = buildDirectUrl();
    if (!contentUrl) return;
    const file = item.agentMdFile || item.contentFile;
    const fileName = file || `${item.name}.md`;
    const uri = `${scheme}://verint.marketplace-installer/install?url=${encodeURIComponent(contentUrl)}&name=${encodeURIComponent(fileName)}&type=${encodeURIComponent(item.type)}&slug=${encodeURIComponent(item.name)}`;
    window.location.assign(uri);
    onTrackDownload?.(scheme);
  };

  const handleWindsurfDeepLink = () => {
    if (item.type === 'template' && item.templateFiles?.length && item.rawBaseUrl) {
      const uri = buildBatchUri('windsurf://verint.marketplace-installer-windsurf', item.templateFiles, item.name);
      window.location.assign(uri);
      onTrackDownload?.('windsurf');
      return;
    }
    const contentUrl = buildDirectUrl();
    if (!contentUrl) return;
    const file = item.agentMdFile || item.contentFile;
    const fileName = file || `${item.name}.md`;
    const uri = `windsurf://verint.marketplace-installer-windsurf/install?url=${encodeURIComponent(contentUrl)}&name=${encodeURIComponent(fileName)}&type=${encodeURIComponent(item.type)}&slug=${encodeURIComponent(item.name)}`;
    window.location.assign(uri);
  };

  const handleIntelliJDeepLink = () => {
    if (item.type === 'template' && item.templateFiles?.length && item.rawBaseUrl) {
      const uri = buildBatchUri('jetbrains://idea/verint-marketplace-installer', item.templateFiles, item.name);
      window.location.assign(uri);
      onTrackDownload?.('intellij');
      return;
    }
    const contentUrl = buildDirectUrl();
    if (!contentUrl) return;
    const file = item.agentMdFile || item.contentFile;
    const fileName = file || `${item.name}.md`;
    const uri = `jetbrains://idea/verint-marketplace-installer/install?url=${encodeURIComponent(contentUrl)}&name=${encodeURIComponent(fileName)}&type=${encodeURIComponent(item.type)}&slug=${encodeURIComponent(item.name)}`;
    window.location.assign(uri);
    onTrackDownload?.('intellij');
  };

  /** Strip query string / fragment then return the last path segment. */
  const vsixFilenameFromUrl = (url: string) => {
    try { return new URL(url).pathname.split('/').pop() || 'marketplace-installer-windsurf.vsix'; }
    catch { return url.split('?')[0].split('#')[0].split('/').pop() || 'marketplace-installer-windsurf.vsix'; }
  };

  const handleCopyWindsurfInstallCommand = () => {
    if (!hasWindsurfAsset) return;
    const windsurfVsixFilename = vsixFilenameFromUrl(windsurfVsixUrl);
    navigator.clipboard.writeText(`windsurf --install-extension ${windsurfVsixFilename}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyInstallCommand = () => {
    if (!hasVsixAsset) return;
    navigator.clipboard.writeText('code --install-extension verint-marketplace-installer.vsix');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyUninstallCommand = () => {
    navigator.clipboard.writeText('code --uninstall-extension verint.marketplace-installer');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle disableTypography className={classes.titleBar}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Install {item.displayName || item.name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.content}>
          <Grid container spacing={3} justifyContent="center">
            {/* ── VS Code extension setup ──────────────────────────── */}
            <Grid item xs={12}>
              <Box marginBottom={3}>
                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box className={classes.accordionSummaryContent}>
                      <Box className={classes.iconBox} style={{ color: '#3b82f6' }}>
                        VS
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                          Install for VS Code
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Set up the Verint Marketplace Installer Extension
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stepper orientation="vertical" style={{ width: '100%' }}>
                      <Step active>
                        <StepLabel>
                          <Typography style={{ fontWeight: 600 }}>Download Extension</Typography>
                        </StepLabel>
                        <StepContent>
                          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                            Download the extension file below.
                          </Typography>
                          {hasVsixAsset ? (
                            <Button
                              href={vsixUrl}
                              variant="contained"
                              size="small"
                              startIcon={<GetAppIcon />}
                              className={classes.btnDownload}
                              download
                            >
                              Download Extension (.vsix)
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<GetAppIcon />}
                              className={classes.btnDownload}
                              disabled
                            >
                              Download Extension (.vsix)
                            </Button>
                          )}
                          {!hasVsixAsset && (
                            <Typography className={classes.unavailableNote}>
                              VS Code installer download is not configured in this environment yet.
                            </Typography>
                          )}
                        </StepContent>
                      </Step>
                      <Step active>
                        <StepLabel>
                          <Typography style={{ fontWeight: 600 }}>Install in VS Code</Typography>
                        </StepLabel>
                        <StepContent>
                          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                            In VS Code, open the <strong>Extensions</strong> panel (<code>Ctrl+Shift+X</code>),
                            click the <strong>⋯</strong> menu, choose <strong>"Install from VSIX…"</strong>,
                            and select the downloaded file.
                          </Typography>
                          <Typography variant="body2" color="textSecondary" style={{ marginTop: 12, marginBottom: 8 }}>
                            Or open a terminal in your Downloads folder and run:
                          </Typography>
                          <Paper
                            elevation={0}
                            className={classes.codeBlock}
                            onClick={hasVsixAsset ? handleCopyInstallCommand : undefined}
                            style={!hasVsixAsset ? { cursor: 'default', opacity: 0.55 } : undefined}
                          >
                            <span>code --install-extension verint-marketplace-installer.vsix</span>
                            <FileCopyOutlinedIcon style={{ fontSize: 14, opacity: 0.8, marginLeft: 8 }} />
                          </Paper>
                        </StepContent>
                      </Step>
                      <Step active>
                        <StepLabel>
                          <Typography style={{ fontWeight: 600, color: '#22c55e' }}>Ready!</Typography>
                        </StepLabel>
                      </Step>
                    </Stepper>
                  </AccordionDetails>
                </Accordion>
              </Box>

              {/* ── Windsurf extension setup (agents, prompts, skills, rules only) ── */}
              {isWindsurfSupported && (
              <Box marginBottom={2}>
                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box className={classes.accordionSummaryContent}>
                      <Box className={classes.iconBox} style={{ color: '#7c3aed' }}>
                        WS
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                          Install for Windsurf
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Set up the Verint Marketplace Installer Extension for Windsurf
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stepper orientation="vertical" style={{ width: '100%' }}>
                      <Step active>
                        <StepLabel>
                          <Typography style={{ fontWeight: 600 }}>Download Extension</Typography>
                        </StepLabel>
                        <StepContent>
                          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                            Download the Windsurf-specific extension file below.
                          </Typography>
                          {hasWindsurfAsset ? (
                            <Button
                              href={windsurfVsixUrl}
                              variant="contained"
                              size="small"
                              startIcon={<GetAppIcon />}
                              className={classes.btnDownloadWindsurf}
                              download
                            >
                              Download Extension (.vsix)
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<GetAppIcon />}
                              className={classes.btnDownloadWindsurf}
                              disabled
                            >
                              Download Extension (.vsix)
                            </Button>
                          )}
                          {!hasWindsurfAsset && (
                            <Typography className={classes.unavailableNote}>
                              Windsurf installer download is not configured in this environment yet.
                            </Typography>
                          )}
                        </StepContent>
                      </Step>
                      <Step active>
                        <StepLabel>
                          <Typography style={{ fontWeight: 600 }}>Install in Windsurf</Typography>
                        </StepLabel>
                        <StepContent>
                          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                            In Windsurf, open the <strong>Extensions</strong> panel (<code>Ctrl+Shift+X</code>),
                            click the <strong>⋯</strong> menu, choose <strong>"Install from VSIX…"</strong>,
                            and select the downloaded file.
                          </Typography>
                          <Typography variant="body2" color="textSecondary" style={{ marginTop: 12, marginBottom: 8 }}>
                            Or open a terminal in your Downloads folder and run:
                          </Typography>
                          <Paper
                            elevation={0}
                            className={classes.codeBlock}
                            onClick={hasWindsurfAsset ? handleCopyWindsurfInstallCommand : undefined}
                            style={!hasWindsurfAsset ? { cursor: 'default', opacity: 0.55 } : undefined}
                          >
                            <span>windsurf --install-extension {vsixFilenameFromUrl(windsurfVsixUrl)}</span>
                            <FileCopyOutlinedIcon style={{ fontSize: 14, opacity: 0.8, marginLeft: 8 }} />
                          </Paper>
                        </StepContent>
                      </Step>
                      <Step active>
                        <StepLabel>
                          <Typography style={{ fontWeight: 600, color: '#22c55e' }}>Ready!</Typography>
                        </StepLabel>
                      </Step>
                    </Stepper>
                  </AccordionDetails>
                </Accordion>
              </Box>
              )}

              {/* ── IntelliJ plugin setup ──────────────────────────── */}
              <Accordion className={classes.accordion}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box className={classes.accordionSummaryContent}>
                    <Box className={classes.iconBox} style={{ color: '#F01E69' }}>
                      IJ
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                        Install for IntelliJ
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Set up the Verint Marketplace Installer Plugin
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stepper orientation="vertical" style={{ width: '100%' }}>
                    <Step active>
                      <StepLabel>
                        <Typography style={{ fontWeight: 600 }}>Download Plugin</Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                          Download the setup package (contains the plugin and install scripts).
                        </Typography>
                        {hasIntelliJAsset ? (
                          <Button
                            href={intellijZipUrl}
                            variant="contained"
                            size="small"
                            startIcon={<GetAppIcon />}
                            style={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#F01E69', color: '#fff' }}
                            download
                          >
                            Download Setup ZIP
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<GetAppIcon />}
                            style={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#F01E69', color: '#fff' }}
                            disabled
                          >
                            Download Setup ZIP
                          </Button>
                        )}
                        {!hasIntelliJAsset && (
                          <Typography className={classes.unavailableNote}>
                            IntelliJ installer download is not configured in this environment yet.
                          </Typography>
                        )}
                      </StepContent>
                    </Step>
                    <Step active>
                      <StepLabel>
                        <Typography style={{ fontWeight: 600 }}>Install in IntelliJ</Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                          Unzip the downloaded file and double-click <strong>install.bat</strong> (Windows)
                          or run <strong>./install.sh</strong> (macOS/Linux).
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          The script will auto-detect your IntelliJ plugins directory and install the plugin.
                        </Typography>
                      </StepContent>
                    </Step>
                    <Step active>
                      <StepLabel>
                        <Typography style={{ fontWeight: 600 }}>Restart &amp; Use</Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2" color="textSecondary">
                          Restart IntelliJ IDEA and authorize with GitHub via{' '}
                          <strong>Tools → Verint Marketplace Installer → Authorize with GitHub</strong>
                        </Typography>
                      </StepContent>
                    </Step>
                  </Stepper>
                </AccordionDetails>
              </Accordion>

              {/* ── How to Uninstall ──────────────────────────── */}
              <Box marginTop={3}>
                <Accordion className={classes.accordion}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box className={classes.accordionSummaryContent}>
                      <Box className={classes.iconBox} style={{ color: '#94a3b8' }}>
                        ✕
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                          How to Uninstall
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Remove the artifact or installer extension
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stepper orientation="vertical" style={{ width: '100%' }}>
                      <Step active>
                        <StepLabel>
                          <Typography style={{ fontWeight: 600 }}>Uninstall from VS Code</Typography>
                        </StepLabel>
                        <StepContent>
                          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                            <strong>Remove an installed artifact:</strong> Delete the corresponding file from your workspace&apos;s <strong>.github/</strong> folder (e.g. <strong>.agent.md</strong>, <strong>.instructions.md</strong>, <strong>.prompt.md</strong>).
                          </Typography>
                          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                            <strong>Remove the installer extension</strong> via the Extensions panel (<strong>Ctrl+Shift+X</strong>) → find "Verint Marketplace Installer" → gear icon → <strong>Uninstall</strong>. Or run:
                          </Typography>
                          <Paper elevation={0} className={classes.codeBlock} onClick={handleCopyUninstallCommand}>
                            <span>code --uninstall-extension verint.marketplace-installer</span>
                            <FileCopyOutlinedIcon style={{ fontSize: 14, opacity: 0.8, marginLeft: 8 }} />
                          </Paper>
                        </StepContent>
                      </Step>
                      {isWindsurfSupported && (
                      <Step active>
                        <StepLabel>
                          <Typography style={{ fontWeight: 600 }}>Uninstall from Windsurf</Typography>
                        </StepLabel>
                        <StepContent>
                          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                            <strong>Remove an installed artifact:</strong> Delete the corresponding file from your workspace&apos;s <strong>.github/</strong> folder (e.g. <strong>.agent.md</strong>, <strong>.instructions.md</strong>).
                          </Typography>
                          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                            <strong>Remove the installer extension</strong> via the Extensions panel (<strong>Ctrl+Shift+X</strong>) → find "Verint Marketplace Installer (Windsurf)" → gear icon → <strong>Uninstall</strong>.
                          </Typography>
                        </StepContent>
                      </Step>
                      )}
                      <Step active>
                        <StepLabel>
                          <Typography style={{ fontWeight: 600 }}>Uninstall from IntelliJ IDEA</Typography>
                        </StepLabel>
                        <StepContent>
                          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                            <strong>Remove an installed artifact:</strong> Delete the corresponding file from your project directory.
                          </Typography>
                          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                            <strong>Remove the installer plugin:</strong> Go to <strong>Settings → Plugins → Installed</strong>, find "Verint Marketplace Installer", click the gear icon, and select <strong>Uninstall</strong>.
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Restart IntelliJ IDEA to complete removal.
                          </Typography>
                        </StepContent>
                      </Step>
                    </Stepper>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Grid>

            {/* ── Action cards ─────────────────────────────────────── */}
            {isWindsurfSupported && (
            <Grid item xs={12} md={6}>
              <Paper elevation={0} className={`${classes.ideCard} ${classes.ideCardWindsurf}`}>
                <Box className={classes.ideIcon} style={{ color: '#7c3aed' }}>
                  <span style={{ fontSize: '1.7rem', fontWeight: 800 }}>WS</span>
                </Box>
                <Typography variant="h6" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
                  Windsurf
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24, lineHeight: 1.6 }}>
                  Install directly using the Verint Extension protocol handler for Windsurf.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  className={classes.btnWindsurf}
                  onClick={handleWindsurfDeepLink}
                  disabled={!canLaunchWindsurf}
                  title={windsurfUnavailableReason || undefined}
                >
                  Open in Windsurf
                </Button>
                {windsurfUnavailableReason && (
                  <Typography className={classes.unavailableNote}>
                    {windsurfUnavailableReason}
                  </Typography>
                )}
              </Paper>
            </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Paper elevation={0} className={`${classes.ideCard} ${classes.ideCardVscode}`}>
                <Box className={classes.ideIcon} style={{ color: '#3b82f6' }}>
                  <span style={{ fontSize: '2rem', fontWeight: 800 }}>VS</span>
                </Box>
                <Typography variant="h6" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
                  VS Code
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24, lineHeight: 1.6 }}>
                  Install directly using the Verint Extension protocol handler.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  className={classes.btnVscode}
                  onClick={() => handleDeepLink('vscode')}
                  disabled={!canLaunchVsCode}
                  title={vsCodeUnavailableReason || undefined}
                >
                  Open in VS Code
                </Button>
                {vsCodeUnavailableReason && (
                  <Typography className={classes.unavailableNote}>
                    {vsCodeUnavailableReason}
                  </Typography>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} className={`${classes.ideCard} ${classes.ideCardIntellij}`}>
                <Box className={classes.ideIcon} style={{ color: '#F01E69' }}>
                  <span style={{ fontSize: '2rem', fontWeight: 800 }}>IJ</span>
                </Box>
                <Typography variant="h6" gutterBottom style={{ fontWeight: 600, marginBottom: 8 }}>
                  IntelliJ
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24, lineHeight: 1.6 }}>
                  Install directly using the Verint IntelliJ plugin protocol handler.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  className={classes.btnIntellij}
                  onClick={handleIntelliJDeepLink}
                  disabled={!canLaunchIntelliJ}
                  title={intellijUnavailableReason || undefined}
                >
                  Open in IntelliJ
                </Button>
                {intellijUnavailableReason && (
                  <Typography className={classes.unavailableNote}>
                    {intellijUnavailableReason}
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCopied(false)} severity="success" style={{ width: '100%' }}>
          Copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
}
